import { useAuth } from "@/lib/auth";
import DashboardLayout from "@/components/DashboardLayout";
import { Link } from "react-router-dom";
import { Mail, IdCard, ArrowRight, Sparkles, Printer } from "lucide-react";

/** Welcome letter shown to associates on first login (also accessible from sidebar).
 *  Uses the same navy + gold visual DNA — no radical color departure from the approved plan.
 */
export default function Welcome() {
  const { user } = useAuth();
  const today = new Date().toLocaleDateString("en-IN", { weekday: "long", year: "numeric", month: "long", day: "numeric" });

  return (
    <DashboardLayout title="Welcome to Samara Wiz" subtitle="Your official onboarding letter">
      <div className="max-w-3xl mx-auto bg-white border border-line rounded-sm shadow-sm">
        <div className="bg-royal text-white px-8 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Mail size={18} className="text-gold" />
            <span className="font-serif text-lg tracking-wide">Welcome Letter</span>
          </div>
          <div className="text-xs text-white/70">Date: {today}</div>
        </div>
        <div className="px-8 py-10">
          <div className="flex items-center gap-4 pb-6 border-b border-line">
            <div className="w-14 h-14 grid place-items-center bg-gold/15 border border-gold/30 rounded-sm">
              <Sparkles size={22} className="text-gold-deep" />
            </div>
            <div>
              <div className="font-serif text-2xl text-royal leading-none">Samara Wiz Pvt Ltd</div>
              <div className="text-[11px] uppercase tracking-[0.25em] text-gold-deep mt-1">Health · Education · Beauty</div>
            </div>
          </div>

          <p className="mt-8 text-navy/80 leading-relaxed">
            Dear <span className="font-medium text-royal">{user?.name || "Associate"}</span>,
          </p>
          <p className="mt-4 text-navy/80 leading-relaxed">
            It gives us immense pleasure to welcome you to <strong>Samara Wiz Pvt Ltd</strong> — a family that
            strives and works for a race against poverty, offering financial freedom and a happy, healthy life.
          </p>
          <p className="mt-4 text-navy/80 leading-relaxed">
            Below are your Associate ID and other details for accessing your account and any related information
            at our official website <a href="https://www.samarawiz.com" className="text-royal underline hover:text-gold">www.samarawiz.com</a>.
            We recommend you change your password immediately. If any issue occurs while logging in, please email us at{" "}
            <a href="mailto:support@samarawiz.com" className="text-royal underline hover:text-gold">support@samarawiz.com</a>.
          </p>

          <div className="mt-8 border border-line rounded-sm overflow-hidden">
            <table className="w-full text-sm">
              <tbody>
                {[
                  ["Sponsor Name",   user?.sponsor_name || "—"],
                  ["Associate ID",   user?.user_code || "—"],
                  ["Associate Name", user?.name || "—"],
                  ["User Name",      user?.email || "—"],
                  ["Mobile Number",  user?.phone || "—"],
                  ["Address",        user?.address || "—"],
                ].map(([k, v]) => (
                  <tr key={k} className="border-b border-line last:border-0">
                    <td className="bg-ivory px-4 py-3 text-royal font-medium w-1/3">{k}</td>
                    <td className="px-4 py-3 text-navy/80">{v}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p className="mt-6 text-xs text-navy/60 italic text-center border-t border-line pt-4">
            This is the information you shall use for all your correspondence with the company.
          </p>

          <p className="mt-6 text-navy/80 leading-relaxed">
            Samara Wiz is committed to providing you with the best products and services at all times. Your journey
            begins today — from your first pair to Crown-Ambassador rank.
          </p>
          <p className="mt-4 text-navy/80 leading-relaxed">Warm regards,<br />
            <span className="font-serif text-royal">Team Samara Wiz</span>
          </p>

          <div className="mt-10 flex flex-wrap gap-3 pt-6 border-t border-line">
            <Link to="/id-card" data-testid="welcome-idcard" className="px-5 py-3 bg-royal text-white hover:bg-royal-deep rounded-sm inline-flex items-center gap-2 text-sm">
              <IdCard size={16} /> View my ID Card <ArrowRight size={14} />
            </Link>
            <button onClick={() => window.print()} data-testid="welcome-print" className="px-5 py-3 border border-royal/25 text-royal hover:bg-royal/5 rounded-sm inline-flex items-center gap-2 text-sm">
              <Printer size={14} /> Print Letter
            </button>
            <Link to="/password-reset" className="px-5 py-3 border border-royal/25 text-royal hover:bg-royal/5 rounded-sm inline-flex items-center gap-2 text-sm">
              Change Password
            </Link>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
